import React, { useState } from 'react';
import {
  Button, ListSubheader, Menu, MenuItem, TextField, Typography,
} from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';

import type { HandoverPreviewRow, HandoverSection } from '../../../services/admissions/handoverService';
import { fmtClassName } from '../../../utils/pdf/format';

/** "28 / 30 seats" — or "28 enrolled" when the section has no capacity set. */
const seatsLabel = (s: HandoverSection): string =>
  s.capacity ? `${s.enrolled} / ${s.capacity} seats` : `${s.enrolled} enrolled`;

const isFull = (s: HandoverSection): boolean => !!s.capacity && s.enrolled >= s.capacity;

/**
 * The section one applicant is enrolled into. A class without sections
 * shows a quiet dash — it enrolls without one.
 */
export const HandoverSectionSelect: React.FC<{
  row: HandoverPreviewRow;
  value: string | undefined;
  onChange: (sectionId: string) => void;
  disabled?: boolean;
  /** Marks an unpicked section as missing (once the row is selected). */
  required?: boolean;
}> = ({ row, value, onChange, disabled, required }) => {
  if (row.sections.length === 0) {
    return <Typography variant="body2" color="text.disabled">No sections</Typography>;
  }
  return (
    <TextField
      select
      size="small"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      disabled={disabled}
      error={required && !value}
      sx={{ minWidth: 150 }}
      SelectProps={{
        displayEmpty: true,
        renderValue: (v) => {
          const s = row.sections.find(x => x._id === v);
          return s ? `Section ${s.name}` : <Typography component="span" variant="body2" color={required ? 'error' : 'text.secondary'}>Choose section</Typography>;
        },
      }}
      inputProps={{ 'aria-label': `Section for ${row.name}` }}
    >
      {row.sections.map(s => (
        <MenuItem key={s._id} value={s._id}>
          <Typography variant="body2" sx={{ flex: 1, mr: 2 }}>Section {s.name}</Typography>
          <Typography variant="caption" color={isFull(s) ? 'warning.main' : 'text.secondary'}>
            {isFull(s) ? 'Full · ' : ''}{seatsLabel(s)}
          </Typography>
        </MenuItem>
      ))}
    </TextField>
  );
};

/**
 * Puts every selected applicant of a class into one section in a click —
 * one group per class among the selection that has sections.
 */
export const AssignSectionMenu: React.FC<{
  classes: Array<{ classId: string; className: string; sections: HandoverSection[]; count: number }>;
  onAssign: (classId: string, sectionId: string) => void;
  disabled?: boolean;
}> = ({ classes, onAssign, disabled }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (classes.length === 0) return null;
  return (
    <>
      <Button
        size="small"
        endIcon={<KeyboardArrowDown fontSize="small" />}
        onClick={(e) => setAnchor(e.currentTarget)}
        disabled={disabled}
        sx={{ textTransform: 'none' }}
      >
        Assign section to selected
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {classes.flatMap(c => [
          <ListSubheader key={`h-${c.classId}`} sx={{ lineHeight: '32px' }}>
            {fmtClassName(c.className)} · {c.count} selected
          </ListSubheader>,
          ...c.sections.map(s => (
            <MenuItem key={s._id} onClick={() => { onAssign(c.classId, s._id); setAnchor(null); }}>
              <Typography variant="body2" sx={{ flex: 1, mr: 3 }}>Section {s.name}</Typography>
              <Typography variant="caption" color={isFull(s) ? 'warning.main' : 'text.secondary'}>
                {isFull(s) ? 'Full · ' : ''}{seatsLabel(s)}
              </Typography>
            </MenuItem>
          )),
        ])}
      </Menu>
    </>
  );
};
